/* eslint-disable react-refresh/only-export-components */

import { testLogin } from '@/services/Login/auth';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),

  password: z.string().min(1, 'Password is required'),
});

export type LoginFormValues = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormValues) => {
    const response = await testLogin(data.email, data.password);
    console.log(response);
    if (response.statusCode === 200) {
      navigate('/dashboard');
    } else {
      toast.error(response.message);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(135deg,#AB00FF_0%,#00C8EB_100%)] flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-[20px] border border-purple-100 bg-white p-8 shadow-[0_8px_40px_rgba(93,93,246,0.18)]">
        <div className="mb-4 flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[linear-gradient(276deg,#AB00FF_1.89%,#00C8EB_97.31%)]">
            <span className="text-xl font-extrabold text-white">C</span>
          </div>
        </div>

        <h1 className="bg-[linear-gradient(276deg,#AB00FF_1.89%,#00C8EB_97.31%)] bg-clip-text text-center text-2xl font-bold text-transparent">
          Welcome Back
        </h1>

        <p className="mt-2 mb-8 text-center text-sm text-slate-500">
          Sign in to continue to your account
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Username */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-800">Email</label>

            <input
              {...register('email')}
              placeholder="Enter your email"
              className={`
                h-12 w-full rounded-lg border px-4 text-sm outline-none transition-all
                ${
                  errors.email
                    ? 'border-red-500 focus:ring-red-100'
                    : 'border-purple-200 focus:border-purple-600 focus:ring-purple-100'
                }
                focus:ring-4
              `}
            />

            {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-800">Password</label>

            <div className="relative">
              <input
                {...register('password')}
                type="password"
                placeholder="Enter your password"
                className={`
                  h-12 w-full rounded-lg border px-4 pr-12 text-sm outline-none transition-all
                  ${
                    errors.password
                      ? 'border-red-500 focus:ring-red-100'
                      : 'border-purple-200 focus:border-purple-600 focus:ring-purple-100'
                  }
                  focus:ring-4
                `}
              />

              <button
                type="button"
                className="absolute top-1/2 right-4 -translate-y-1/2 text-slate-400 hover:text-purple-600"
              ></button>
            </div>

            {errors.password && (
              <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="h-14 w-full rounded-xl bg-[linear-gradient(276deg,#AB00FF_1.89%,#00C8EB_97.31%)] font-semibold text-white transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
